import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useStore } from '../store';
import { isAdminRole, isSuperAdminRole } from '../lib/auth';

/**
 * Route guard: renders children only for an authenticated (approved) user.
 * While the initial /auth/me hydration is in flight, shows nothing to avoid a
 * login-page flash. Unauthenticated users are redirected to /login, preserving
 * the attempted path in router state so login can bounce them back.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const currentUser = useStore((s) => s.currentUser);
  const authChecked = useStore((s) => s.authChecked);
  const hydrateAuth = useStore((s) => s.hydrateAuth);
  const location = useLocation();

  useEffect(() => {
    if (!authChecked) void hydrateAuth();
  }, [authChecked, hydrateAuth]);

  if (!authChecked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: 'var(--ink-faint)' }}>
        Loading…
      </div>
    );
  }
  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

/**
 * Route guard: requires an authenticated user who can administer their
 * workspace (enterprise_admin, super_admin, or the legacy 'admin' value).
 */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const currentUser = useStore((s) => s.currentUser);
  return (
    <RequireAuth>
      {currentUser && isAdminRole(currentUser.role) ? (
        <>{children}</>
      ) : (
        <Navigate to="/apps" replace />
      )}
    </RequireAuth>
  );
}

/** Route guard: requires the super-admin (platform operator) role. */
export function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const currentUser = useStore((s) => s.currentUser);
  return (
    <RequireAuth>
      {currentUser && isSuperAdminRole(currentUser.role) ? (
        <>{children}</>
      ) : (
        <Navigate to="/apps" replace />
      )}
    </RequireAuth>
  );
}
